import { useQuery } from "@tanstack/react-query";
import type { Vacation } from "@victor/api-client";
import { api } from "../../lib/api";

/**
 * Vague 1 · Phase C.3.b · Bloc rondes intégré dans la VacationCard.
 *
 * Affiche un résumé compact des passages enregistrés sur la vacation +
 * un message explicatif sur le scan QR. Pas de bouton scanner intégré
 * V0 : on s'appuie sur le scanner caméra système (iPhone Camera, Google
 * Lens, etc.) qui reconnaît le QR et propose d'ouvrir l'URL Victor.
 */
export function RondeBlock({ vacation }: { vacation: Vacation }) {
  const passages = useQuery({
    queryKey: ["me-rondes", vacation.id],
    queryFn: () => api.me.rondes(vacation.id),
    enabled: vacation.statut !== "annulee",
  });

  if (vacation.statut === "annulee") return null;

  const list = passages.data ?? [];
  const last = list[list.length - 1] ?? null;

  return (
    <div className="mt-3 border-t border-navy-100 pt-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-navy-500">
          Rondes
        </p>
        <span className="text-xs font-semibold text-navy-400">
          {list.length} {list.length > 1 ? "passages" : "passage"}
        </span>
      </div>

      {last ? (
        <p className="mt-1 text-xs text-navy-500">
          Dernier scan · {formatTime(last.scanneeAt)}
          {last.ecartMetres != null ? ` · à ${last.ecartMetres} m` : ""}
        </p>
      ) : (
        <p className="mt-1 flex items-start gap-2 text-xs text-navy-500">
          <QrIcon />
          <span>
            Scannez le QR de chaque point de contrôle avec votre appareil
            photo.
          </span>
        </p>
      )}
    </div>
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function QrIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="mt-0.5 shrink-0 text-accent-500"
    >
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3h-3z" />
      <path d="M20 14v3" />
      <path d="M14 20h3" />
      <path d="M17 17h4v4" />
    </svg>
  );
}
